/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {Logger} from '@app/lib/Logger';
import RuntimeConfigStore from '@app/stores/RuntimeConfigStore';
import VoiceSettingsStore from '@app/stores/VoiceSettingsStore';
import {DeepFilterNoiseFilterProcessor} from 'deepfilternet3-noise-filter';
import type {LocalAudioTrack} from 'livekit-client';

const logger = new Logger('DeepFilterNoiseProcessor');
const DEFAULT_SUPPRESSION_LEVEL = 100;

let activeProcessor: DeepFilterNoiseFilterProcessor | null = null;
let activeTrack: LocalAudioTrack | null = null;

export function clampSuppressionLevel(level: number): number {
	return Math.max(0, Math.min(100, level));
}

function getSuppressionLevel(): number {
	return clampSuppressionLevel(VoiceSettingsStore.noiseSuppressionLevel);
}

export function createDeepFilterProcessor(level = getSuppressionLevel()): DeepFilterNoiseFilterProcessor {
	return new DeepFilterNoiseFilterProcessor({
		sampleRate: 48000,
		noiseReductionLevel: clampSuppressionLevel(level),
		enabled: true,
		assetConfig: {
			cdnUrl: `${RuntimeConfigStore.staticCdnEndpoint}/libs/deepfilternet3`,
		},
	});
}

export async function applyDeepFilterProcessor(track: LocalAudioTrack): Promise<void> {
	if (!VoiceSettingsStore.getDeepFilterNoiseSuppression()) {
		return;
	}

	try {
		await removeDeepFilterProcessor();

		const processor = createDeepFilterProcessor();
		await track.setProcessor(processor);
		activeTrack = track;
		activeProcessor = processor;
		logger.info('Applied DeepFilterNet3 noise suppression');
	} catch (error) {
		logger.warn('Failed to apply DeepFilterNet3 noise suppression', error);
		activeTrack = null;
		activeProcessor = null;
	}
}

export async function removeDeepFilterProcessor(track?: LocalAudioTrack): Promise<void> {
	if (activeProcessor) {
		const targetTrack = activeTrack ?? track;
		try {
			if (targetTrack) {
				await targetTrack.stopProcessor();
			}
		} catch (error) {
			logger.warn('Failed to stop DeepFilter processor', error);
		}
		activeProcessor.destroy();
		activeTrack = null;
		activeProcessor = null;
		logger.debug('Removed DeepFilterNet3 noise suppression');
	}
}

export function updateSuppressionLevel(level: number): void {
	if (activeProcessor) {
		activeProcessor.setSuppressionLevel(level);
		logger.debug('Updated suppression level', {level});
	}
}

export function setDeepFilterEnabled(enabled: boolean): void {
	if (activeProcessor) {
		activeProcessor.setEnabled(enabled);
		logger.debug('Set DeepFilter enabled', {enabled});
	}
}

export function isDeepFilterActive(): boolean {
	return activeProcessor != null;
}

export function isDeepFilterAppliedToTrack(track?: LocalAudioTrack | null): boolean {
	return activeProcessor != null && activeTrack === (track ?? null);
}

export {DEFAULT_SUPPRESSION_LEVEL};
